import { useMemo } from 'react';

interface BeerGlassProps {
  currentBac: number;
  targetBac: number;
}

const TARGET_POSITION = 85; // Target line always at 85% of glass height

export default function BeerGlass({ currentBac, targetBac }: BeerGlassProps) {
  // Fill level relative to target (when currentBac = targetBac → fill at 85%)
  const fillPercent = useMemo(() => {
    if (targetBac <= 0) return 0;
    return Math.min(100, Math.max(0, (currentBac / targetBac) * TARGET_POSITION));
  }, [currentBac, targetBac]);

  // Target line fixed at 85%
  const targetPercent = TARGET_POSITION;

  // Determine color based on BAC vs target
  const fillColor = useMemo(() => {
    if (currentBac > targetBac) return '#ef4444'; // red - over target
    return '#d4a017'; // beer yellow - normal
  }, [currentBac, targetBac]);

  // Add shake animation when over target
  const isOverTarget = currentBac > targetBac;

  // Glass dimensions for Czech krigl
  const glassTop = 35;
  const glassBottom = 255;
  const glassHeight = glassBottom - glassTop;

  return (
    <div className={`relative ${isOverTarget ? 'animate-shake' : ''}`}>
      <svg
        viewBox="0 0 220 290"
        className="w-full max-w-[220px] mx-auto"
        style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))' }}
      >
        {/* Definitions */}
        <defs>
          {/* Glass gradient - transparent glass effect */}
          <linearGradient id="glassGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
            <stop offset="20%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="80%" stopColor="rgba(255,255,255,0.05)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.15)" />
          </linearGradient>

          {/* Beer gradient */}
          <linearGradient id="beerGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={fillColor} stopOpacity="1" />
            <stop offset="100%" stopColor={fillColor} stopOpacity="0.85" />
          </linearGradient>

          {/* Foam pattern */}
          <pattern id="foamPattern" x="0" y="0" width="20" height="10" patternUnits="userSpaceOnUse">
            <circle cx="5" cy="5" r="5" fill="#fffef0" />
            <circle cx="15" cy="5" r="5" fill="#fffef0" />
            <circle cx="10" cy="2" r="4" fill="#fff" />
          </pattern>

          {/* Handle gradient */}
          <linearGradient id="handleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6b7280" />
            <stop offset="50%" stopColor="#9ca3af" />
            <stop offset="100%" stopColor="#6b7280" />
          </linearGradient>

          {/* Clip path for beer inside Czech krigl - slightly tapered shape */}
          <clipPath id="kriglClip">
            <path d="M35,45 L30,250 Q30,265 95,265 Q160,265 160,250 L155,45 Q155,35 95,35 Q35,35 35,45 Z" />
          </clipPath>

          {/* Facet highlight gradient */}
          <linearGradient id="facetHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {/* Beer fill */}
        <g clipPath="url(#kriglClip)">
          {/* Beer liquid */}
          <rect
            x="25"
            y={glassBottom - (glassHeight * fillPercent / 100)}
            width="140"
            height={glassHeight * fillPercent / 100 + 15}
            fill="url(#beerGradient)"
            className="transition-all duration-500 ease-out"
          />

          {/* Bubbles */}
          {fillPercent > 5 && (
            <>
              <circle cx="55" cy="200" r="3" fill="rgba(255,255,255,0.3)" className="animate-bubble" style={{ animationDelay: '0s' }} />
              <circle cx="95" cy="220" r="2" fill="rgba(255,255,255,0.3)" className="animate-bubble" style={{ animationDelay: '0.5s' }} />
              <circle cx="135" cy="190" r="2.5" fill="rgba(255,255,255,0.3)" className="animate-bubble" style={{ animationDelay: '1s' }} />
              <circle cx="75" cy="210" r="2" fill="rgba(255,255,255,0.3)" className="animate-bubble" style={{ animationDelay: '1.5s' }} />
              <circle cx="115" cy="230" r="3" fill="rgba(255,255,255,0.3)" className="animate-bubble" style={{ animationDelay: '2s' }} />
              <circle cx="65" cy="240" r="2" fill="rgba(255,255,255,0.3)" className="animate-bubble" style={{ animationDelay: '2.5s' }} />
            </>
          )}

          {/* Foam layer */}
          {fillPercent > 10 && (
            <g className="animate-wave">
              <rect
                x="25"
                y={glassBottom - (glassHeight * fillPercent / 100) - 18}
                width="140"
                height="24"
                fill="url(#foamPattern)"
                opacity="0.95"
              />
            </g>
          )}
        </g>

        {/* Czech krigl outline - classic shape with slight taper */}
        <path
          d="M35,45 L30,250 Q30,265 95,265 Q160,265 160,250 L155,45 Q155,35 95,35 Q35,35 35,45 Z"
          fill="url(#glassGradient)"
          stroke="#6b7280"
          strokeWidth="3"
        />

        {/* Thick bottom - characteristic for Czech beer mugs */}
        <ellipse cx="95" cy="262" rx="65" ry="8" fill="rgba(255,255,255,0.1)" stroke="#6b7280" strokeWidth="2" />
        <rect x="30" y="258" width="130" height="8" fill="rgba(100,100,100,0.2)" />

        {/* Facets - vertical lines simulating dimpled/cut glass */}
        <g stroke="rgba(255,255,255,0.15)" strokeWidth="1">
          <line x1="50" y1="50" x2="45" y2="255" />
          <line x1="70" y1="50" x2="65" y2="255" />
          <line x1="90" y1="50" x2="90" y2="255" />
          <line x1="110" y1="50" x2="115" y2="255" />
          <line x1="130" y1="50" x2="135" y2="255" />
          <line x1="150" y1="50" x2="155" y2="255" />
        </g>

        {/* Facet highlights - gives 3D cut glass effect */}
        <g fill="url(#facetHighlight)" opacity="0.5">
          <rect x="52" y="55" width="15" height="195" rx="2" />
          <rect x="92" y="55" width="15" height="195" rx="2" />
          <rect x="132" y="55" width="15" height="195" rx="2" />
        </g>

        {/* Glass rim - thicker for krigl */}
        <ellipse cx="95" cy="38" rx="62" ry="7" fill="none" stroke="#9ca3af" strokeWidth="3" />
        <ellipse cx="95" cy="40" rx="60" ry="6" fill="rgba(255,255,255,0.1)" />

        {/* Massive handle - typical Czech mug style */}
        <path
          d="M158,70
             Q175,70 182,85
             Q190,105 190,140
             Q190,175 182,195
             Q175,210 158,210"
          fill="none"
          stroke="#4b5563"
          strokeWidth="14"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Handle inner highlight */}
        <path
          d="M158,75
             Q172,75 178,88
             Q185,105 185,140
             Q185,175 178,192
             Q172,205 158,205"
          fill="none"
          stroke="url(#handleGradient)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Handle inner dark line for depth */}
        <path
          d="M160,85
             Q168,85 172,95
             Q178,110 178,140
             Q178,170 172,185
             Q168,195 160,195"
          fill="none"
          stroke="rgba(0,0,0,0.2)"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Glass shine effect */}
        <path
          d="M40,60 Q42,150 38,240"
          fill="none"
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Target line - inside glass walls */}
        <line
          x1="38"
          y1={glassBottom - (glassHeight * targetPercent / 100)}
          x2="152"
          y2={glassBottom - (glassHeight * targetPercent / 100)}
          stroke="#ef4444"
          strokeWidth="2"
          strokeDasharray="8,4"
          className="transition-all duration-300"
        />
      </svg>
    </div>
  );
}
